import React, { useCallback, useMemo, useContext } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Field, useField, useForm, useFormState } from 'react-final-form';
import { Loader } from 'ncoded-component-library';
import InputField from 'components/InputField';
import DropzoneField from 'components/DropzoneField';
import validations from 'validations';
import PhoneFieldWrapper from 'components/PhoneFieldWrapper';
import { User } from 'models/User';
import {
  getContactNumber,
  getInitialPhone,
  Phone,
  phoneIsEqual,
} from 'phoneNumberUtils';
import api from 'api';
import InvestorVerify from './components/InvestorVerify';
import CurrentProfileContext from '../../providers/CurrentProfile/CurrentProfile.context';
import Company from '../Company';
import ProfileActionsWithVerification from './components/ProfileActionsWithVerification';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import EmailField from 'components/EmailField';
import PhoneVerificationModal from 'components/PhoneVerificationModal';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import TemporaryNotifications from '../TemporaryNotifications';
import useMatchMedia from 'hooks/useMatchMedia';
import useValidations from 'router/subrouters/Auth/hooks/useValidations';
import FloatingActions from 'components/FloatingActions';
import useProfileForm, { ProfileForm } from '../../hooks/useProfileForm';
import { NAME_MAX_LENGTH } from 'data/global';
import { scrollToErrorsOnSubmit } from 'utils';
import { stringIsEqual } from '../../utils';
import usePhoneNumberTransfer, {
  PHONE_TRANSFER_MODE,
} from 'hooks/usePhoneNumberTransfer';
import PhoneTransferModal from 'components/PhoneTransferModal';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { SUCCESS_MESSAGES } from 'request-responses';

import './ProfilePage.styles.scss';
import './ProfilePage.styles.responsive.scss';

type ProfilePageProps = {
  className?: string;
};

type ProfileDetailsProps = ProfilePageProps & {
  phoneTransfer: ReturnType<typeof usePhoneNumberTransfer>;
};

const ProfileDetails: React.FC<ProfileDetailsProps> = (props) => {
  const { className, phoneTransfer } = props;

  const { popSuccess } = useContext(PopNotificationsContext);
  const { isSuperAdmin, updateCurrentUser } = useContext(CurrentUserContext);
  const { currentProfile, updateProfilePicture } = useContext(
    CurrentProfileContext,
  );

  const [phoneVerificationModal, phoneVerificationModalRef] =
    useCallbackRef<ModalRef>();

  const classes = classNames('profile-page', className);

  const { change } = useForm();

  const {
    values: { phone, hasVerifiedContactNumber },
  } = useFormState<User>();

  const {
    meta: { dirty: contactChanged, error: contactError },
  } = useField<Phone>('phone');

  const {
    meta: { dirty: emailChanged },
  } = useField('email');

  const verifiedPhone = useMemo(
    () => hasVerifiedContactNumber && !contactChanged,
    [contactChanged, hasVerifiedContactNumber],
  );

  // Only for the number still on file after someone else claimed it - once the
  // user types a different one, this is an ordinary unverified number again.
  const verifiedElsewhere = useMemo(
    () => !!currentProfile?.isContactNumberVerifiedElsewhere && !contactChanged,
    [contactChanged, currentProfile?.isContactNumberVerifiedElsewhere],
  );

  const verifiedEmail = useMemo(
    () => currentProfile?.hasVerifiedEmail && !emailChanged,
    [currentProfile?.hasVerifiedEmail, emailChanged],
  );

  const disabledVerification = useMemo(
    () => !phone?.phoneNumber || contactError,
    [contactError, phone],
  );

  const hasVerificationMobile = useMemo(() => !isSuperAdmin, [isSuperAdmin]);

  // Both conflict states belong to one specific number, so they drop away as
  // soon as a different one is typed.
  const conflictMatchesField = useMemo(
    () => getContactNumber(phone) === phoneTransfer.contactNumber,
    [phone, phoneTransfer.contactNumber],
  );

  const isTablet = useMatchMedia('isTablet');

  const { t } = useTranslation();

  // The rejected PATCH saved nothing, so the form stays dirty and the save bar
  // stays up - clearing the conflict simply re-enables it for the user to save
  // again, now that the number is theirs.
  const onNumberConfirmed = useCallback(
    (user: User) => {
      phoneTransfer.reset();
      updateCurrentUser(user);
      popSuccess(t(SUCCESS_MESSAGES.PHONE_NUMBER_VERIFIED));
    },
    [phoneTransfer, popSuccess, t, updateCurrentUser],
  );

  const onAddAnotherNumber = useCallback(() => {
    phoneTransfer.reset();
    change('phone', { ...getInitialPhone(), phoneNumber: '' } as Phone);
  }, [change, phoneTransfer]);

  const commonValidations = useValidations();

  const uploadFile = useCallback(
    async (file, fileName, config) => {
      const data = await api.users.uploadFile(
        currentProfile?._id,
        file,
        fileName,
        config,
      );
      updateProfilePicture(data);
      return data;
    },
    [currentProfile?._id, updateProfilePicture],
  );

  const deleteFiles = useCallback(
    async (file, fileName) => {
      const {
        data: { fileId },
      } = await api.users.deleteFile(currentProfile?._id, fileName, file.id);
      updateProfilePicture(null);
      return fileId;
    },
    [currentProfile?._id, updateProfilePicture],
  );

  const profileImageField = useMemo(
    () => (
      <Field
        label={t('profileImage')}
        name="profileImage"
        fileName=""
        className="profile-image"
        component={DropzoneField}
        uploadRequest={uploadFile}
        deleteRequest={deleteFiles}
        maxFiles={1}
        accept="image/*"
        onDeleteMessage={{
          title: t('deleteProfileImage'),
          content: t('deleteProfileImageDescription'),
        }}
      />
    ),
    [deleteFiles, t, uploadFile],
  );

  return (
    <div className="profile-page__wrapper">
      <div className={classes}>
        <PhoneVerificationModal
          ref={phoneVerificationModalRef}
          phone={phone}
          disabled={disabledVerification}
          onServerError={phoneTransfer.onServerError}
          // The number is ours now, so the conflict it raised is spent.
          onSuccess={phoneTransfer.reset}
        />

        <PhoneTransferModal
          ref={phoneTransfer.modalRef}
          contactNumber={phoneTransfer.contactNumber}
          initiate={phoneTransfer.initiate}
          check={phoneTransfer.check}
          onSuccess={onNumberConfirmed}
          onServerError={phoneTransfer.onServerError}
        />

        <div className="profile-page__profile-details profile-card-container">
          <p className="profile-section-title">{t('contactInformation')}</p>
          <div className="form-row">
            <Field
              name="firstName"
              label={t('firstName')}
              component={InputField}
              required
              validate={commonValidations.name}
            />
            <Field
              name="lastName"
              label={t('lastName')}
              component={InputField}
              required
              validate={commonValidations.name}
            />
          </div>
          <div className="form-row">
            <Field
              name="title"
              label={t('title')}
              placeholder={t('businessBrokerAndManager')}
              component={InputField}
              isEqual={stringIsEqual}
              validate={validations.noLongerThan(
                NAME_MAX_LENGTH,
                t('noLongerThan', { length: NAME_MAX_LENGTH }),
              )}
            />
            <EmailField required isVerified={verifiedEmail} />
          </div>
          <div className="form-row">
            <PhoneFieldWrapper
              verified={verifiedPhone}
              verifiedElsewhere={verifiedElsewhere}
              conflict={conflictMatchesField && phoneTransfer.isConflict}
              limitReached={
                conflictMatchesField && phoneTransfer.isLimitReached
              }
              isEqual={phoneIsEqual}
              onConfirmNumber={phoneTransfer.openModal}
              onAddAnotherNumber={onAddAnotherNumber}
              {...(hasVerificationMobile && {
                onVerification: phoneVerificationModal?.open,
              })}
            />
            <Field
              name="linkedinAccount"
              label={t('linkedinAccount')}
              component={InputField}
              isEqual={stringIsEqual}
              validate={validations.linkedinValidation()}
            />
          </div>

          <ProfileActionsWithVerification />

          {isTablet && profileImageField}
        </div>

        <TemporaryNotifications />

        <Company />

        <div className="profile-card-container">
          <InvestorVerify />
        </div>
      </div>
      {!isTablet && profileImageField}
    </div>
  );
};

const ProfilePage: React.FC<ProfilePageProps> = (props) => {
  const { className } = props;

  const { t } = useTranslation();

  // Lives above the form so the save bar can be held back while a conflict is
  // unresolved - saving again would just hit the same rejection.
  const phoneTransfer = usePhoneNumberTransfer({
    mode: PHONE_TRANSFER_MODE.PROFILE,
  });

  // The save itself is what reports the conflict: the PATCH is rejected with
  // PHONE_NUMBER_IN_USE_ON_ANOTHER_ACCOUNT and nothing is persisted.
  const { isLoading, initialData, onSubmit } = useProfileForm({
    onPatchError: phoneTransfer.onServerError,
  });

  return (
    <ProfileForm
      keepDirtyOnReinitialize
      initialValues={initialData}
      onSubmit={onSubmit}
      render={({
        pristine,
        invalid,
        submitting,
        values,
        form: { submit },
        handleSubmit,
      }) => {
        // Both conflict states belong to one specific number, so they drop away
        // as soon as a different one is typed.
        const isConflict =
          phoneTransfer.isConflict &&
          getContactNumber(values.phone) === phoneTransfer.contactNumber;

        return (
          <form onSubmit={handleSubmit} className="profile-form">
            {isLoading && <Loader className="fixed without-header" />}

            <ProfileDetails
              className={className}
              phoneTransfer={phoneTransfer}
            />

            <FloatingActions
              isVisible={!pristine}
              disabled={pristine || submitting || isConflict}
              message={t('unsavedChanges')}
              onSubmit={() => scrollToErrorsOnSubmit(invalid, submit)}
            />
          </form>
        );
      }}
    />
  );
};

export default ProfilePage;
